import React, { Fragment, Suspense, useCallback, useEffect, useRef, useState, useDeferredValue } from 'react';
import { find, get, map, isEqual, compact, toLower, some, forEach, filter, includes } from 'lodash';
import {
  Box,
  Spinner,
  SpinnerSize,
  Heading,
  Button,
  ButtonAppearance,
  ButtonSize,
  IconSize,
  useModalState,
  Text,
  TextFieldSize,
  Icon,
} from '@bechtel/gds';
import {
  ErrorBoundary,
  useSessionStorage,
  CacheKeys,
  TextWithEllipsis,
  EllipsisPlacement,
  useAppUser,
} from '@bechtel/client-common';
import {
  ListStyled,
  NavLinkStyled,
  SearchFieldStyled,
  SearchCanvasButtonStyled,
  SearchFieldComponentStyled,
  StyledIcon,
  WeldDetailContainerStyled,
  WeldListHeader,
  WeldListContainerStyled,
} from './styled';
// @ts-ignore: tsc declarations not recognising types/index.d.ts
import { ReactComponent as FilterIcon } from '@bechtel/client-assets/dist/images/filter.svg';
// @ts-ignore: tsc declarations not recognising types/index.d.ts
import { ReactComponent as EmptyWeldDetailIcon } from '@bechtel/client-assets/dist/images/empty-weld-detail.svg';
import { t, Trans } from '@lingui/macro';
import { WeldItemCard } from '~/components/organisms/weld-item-card';
import {
  Weld,
  CommoditySortByTerm,
  SortOrder,
  CommodityFilterByTerm,
  Project,
  FilterOperator,
  CommodityComplexFilterInput,
  CommodityFilterInput,
  CommodityFilterStatusFlag,
  CommodityList,
  PagingInput,
  PersonaType,
} from '@bechtel/types';
import { NetworkStatus, useLazyQuery } from '@apollo/client';
import { GET_WELDS } from '@bechtel/client-gql';
import {
  weldSizeList,
  weldStatusList,
  WeldDrawerValuesProps,
  drawerValues as defaultValues,
  searchScopeList,
  searchByList,
  WeldSearchDrawer,
  weldTypes,
  DrawerSearchScope,
  sortByList,
} from '~/components/organisms/weld-search-drawer';
import { useProjectCommodityTypesById } from '@bechtel/client-weld-common';
import { useLocation, useNavigate, NavigationType, useNavigationType } from 'react-router-dom';

export const WeldListComponent = () => {
  const { user } = useAppUser();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  // NOTE:  used for toggling advanced search drawer
  const { close, toggle, visible } = useModalState(false);
  const [totalWelds, setTotalWelds] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  // NOTE: we need a useRef here otherwise route transitions will "reset" the state for drawerValues and fall back to the "defaultValues"
  const persistedDrawerValues = useRef(defaultValues);
  const [drawerValues, setDrawerValues] = useSessionStorage<WeldDrawerValuesProps>(
    CacheKeys.WeldDrawerValues,
    persistedDrawerValues.current || defaultValues
  );
  const currentProjectWeek = find(
    user?.project?.projectConfiguration?.projectWeeks,
    (projectWeek) => projectWeek?.isCurrent
  );

  const debouncedSearchQuery = useDeferredValue(searchQuery?.trim());
  const [
    getProject,
    { loading: projectLoading, called: projectCalled, data: projectData },
  ] = useProjectCommodityTypesById();
  const weldsLimit = 20;
  const projectDetailArr: Project[] = get(projectData, 'projectsByIds', []);
  const projectDetail = projectDetailArr.length > 0 ? projectDetailArr[0] : null;
  const commdityStatuses = [
    CommodityFilterStatusFlag.READY_TO_WELD,
    CommodityFilterStatusFlag.VT_SENT_BACK,
    CommodityFilterStatusFlag.FITUP_INSPECTION,
  ];
  const weldTypeId = find(projectDetail?.projectConfiguration.projectCommodityTypes, (item) => {
    return toLower(item.name) === 'weld';
  })?.id;

  const [loadWelds, { loading, data, error, refetch, networkStatus, called, fetchMore }] = useLazyQuery<
    { commodities: CommodityList },
    {
      input: CommodityFilterInput;
      paging: PagingInput;
    }
  >(GET_WELDS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    onCompleted: (data) => {
      setTotalWelds(data?.commodities?.total);
    },
  });

  useEffect(() => {
    if (!user || !user.project || projectCalled) return;
    getProject({
      variables: {
        ids: [user?.project?.id],
      },
    });
  }, [user]);

  useEffect(() => {
    if (!weldTypeId) return;
    if (debouncedSearchQuery.length > 0 && debouncedSearchQuery.length < 3) return;
    const weldComplexFilter: CommodityComplexFilterInput = {
      and: [
        {
          term: CommodityFilterByTerm.COMMODITY_TYPE_ID,
          value: weldTypeId,
          operator: FilterOperator.EQUALS,
        },
      ],
      or: [],
    };
    if (user?.crewMember?.persona && user.crewMember.persona === PersonaType.FIELD_WELD_ENGINEER) {
      weldComplexFilter.and?.push({
        term: CommodityFilterByTerm.PERSONA_TYPE_ID,
        value: user.crewMember.persona,
        operator: FilterOperator.EQUALS,
      });
    }
    if (drawerValues?.weldTypes?.length > 0)
      weldComplexFilter.and?.push({
        term: CommodityFilterByTerm.CONNECTION_TYPE,
        values: drawerValues?.weldTypes,
        operator: FilterOperator.IN,
      });

    if (drawerValues?.weldSizeList?.length === 1) {
      const isSmallBore = some(drawerValues?.weldSizeList, (item) => {
        return item === CommodityFilterByTerm.SMALL_BORE;
      });
      weldComplexFilter.and?.push({
        term: CommodityFilterByTerm.NOMINAL_DIAMETER,
        value: '2.5',
        operator: isSmallBore ? FilterOperator.LESS_THAN : FilterOperator.GREATER_THAN,
      });
    }

    if (drawerValues?.weldStatusList?.length > 0) {
      const includeBlockedState = drawerValues?.weldStatusList.find((weldStatusItem) => {
        return commdityStatuses.includes(weldStatusItem);
      });

      weldComplexFilter.and?.push({
        term: CommodityFilterByTerm.COMMODITY_STATUS_ID,
        values: drawerValues?.weldStatusList,
        operator: FilterOperator.IN,
      });

      if (location?.state?.applyBlockedFilter || includeBlockedState) {
        weldComplexFilter.and?.push({
          term: CommodityFilterByTerm.INCLUDE_BLOCKED_COMMODITY,
          value: location?.state?.applyBlockedFilter ? location?.state?.applyBlockedFilter : 'true',
          operator: FilterOperator.EQUALS,
        });
      }
    }

    if (drawerValues?.isPriority) {
      weldComplexFilter.and?.push({
        term: CommodityFilterByTerm.PRIORITY_WELDS,
        value: 'true',
        operator: FilterOperator.EQUALS,
      });
    }

    if (
      drawerValues?.searchScope === DrawerSearchScope.ASSIGNED_TO_ME ||
      drawerValues?.searchScope === DrawerSearchScope.UNASSIGNED ||
      drawerValues?.searchScope === DrawerSearchScope.ALL
    ) {
      weldComplexFilter.and?.push({
        term: CommodityFilterByTerm.PRIMARY_CREW_MEMBER_ID,
        value: user?.crewMember?.id,
        operator: FilterOperator.EQUALS,
      });
    }

    if (drawerValues?.searchScope === DrawerSearchScope.ASSIGNED_TO_ME) {
      weldComplexFilter.and?.push({
        term: CommodityFilterByTerm.ASSIGNED_CREW_MEMBER,
        value: 'true',
        operator: FilterOperator.EQUALS,
      });
    }

    if (drawerValues?.searchScope === DrawerSearchScope.UNASSIGNED) {
      weldComplexFilter.and?.push({
        term: CommodityFilterByTerm.UNASSIGNED_COMMODITIES,
        value: 'true',
        operator: FilterOperator.EQUALS,
      });
      if (user?.crewMember?.persona && user.crewMember.persona != PersonaType.FIELD_WELD_ENGINEER) {
        weldComplexFilter.and?.push({
          term: CommodityFilterByTerm.PERSONA_TYPE_ID,
          value: user?.crewMember?.persona,
          operator: FilterOperator.EQUALS,
        });
      }
    }

    if (
      user?.crewMember?.persona &&
      user.crewMember.persona === PersonaType.FIELD_WELD_ENGINEER &&
      drawerValues?.isSupportingForeman
    ) {
      weldComplexFilter.and?.push({
        term: CommodityFilterByTerm.APPLY_ASSOCIATED_CREW_MEMBERS,
        value: 'true',
        operator: FilterOperator.EQUALS,
      });
    }
    if (location?.state?.isWeeklyStats) {
      weldComplexFilter.and?.push(
        {
          term: CommodityFilterByTerm.START_DATE,
          value: currentProjectWeek?.startDate,
          operator: FilterOperator.GREATER_THAN,
        },
        {
          term: CommodityFilterByTerm.END_DATE,
          value: currentProjectWeek?.endDate,
          operator: FilterOperator.LESS_THAN,
        }
      );
    }

    if (debouncedSearchQuery)
      forEach(drawerValues.searchByList, (item: CommodityFilterByTerm) => {
        weldComplexFilter.or?.push({
          term: item,
          value: debouncedSearchQuery,
          operator: item == CommodityFilterByTerm.CREW_MEMBER_NAME ? FilterOperator.FUZZY : FilterOperator.CONTAINS,
        });
      });

    loadWelds({
      variables: {
        input: {
          complexFilter: weldComplexFilter,
          sortByFilter: {
            term: drawerValues?.sortBy ? drawerValues?.sortBy : CommoditySortByTerm.TAG,
            sortOrder: drawerValues?.sortDir ? drawerValues?.sortDir : SortOrder.DESC,
          },
        },
        paging: {
          cursor: '1',
          limit: weldsLimit,
        },
      },
    });
  }, [weldTypeId, debouncedSearchQuery, drawerValues, loadWelds, user?.crewMember?.id]);

  const onDrawerSubmit = useCallback(
    (values: WeldDrawerValuesProps) => {
      setDrawerValues(() => {
        persistedDrawerValues.current = values;
        return values;
      });
    },
    [setDrawerValues]
  );

  // NOTE: allows us to safely access "data.commodities.items" during all lifecycles
  const weldList: Weld[] = map(get(data, 'commodities.items', []), 'weld');
  const totalPages = totalWelds ? Math.ceil(totalWelds / weldsLimit) : 0;
  const weldListCursor = get(data, 'commodities.cursor', 1);

  const handleScroll = (e) => {
    if (
      e.target.scrollTop + e.target.clientHeight + 200 >= e.target.scrollHeight &&
      weldListCursor <= totalPages &&
      !loading
    ) {
      fetchMore({
        variables: {
          paging: {
            cursor: weldListCursor.toString(),
            limit: weldsLimit,
          },
        },
      });
    }
  };

  useEffect(() => {
    if (weldList.length > 0) {
      if (location.pathname === '/welds') {
        if (navigationType === NavigationType.Pop) {
          navigate(-1);
        } else {
          const [weld] = weldList;
          const encodedId = encodeURIComponent(weld.id);
          navigate(`${encodedId}`);
        }
      }
    }
  }, [location.pathname, navigate, navigationType, weldList]);
  const searchingByLabel = map(drawerValues.searchByList, (search: any) => {
    return get(
      find(searchByList, {
        value: search,
      }),
      'label'
    );
  });

  const searchingLabel = get(
    find(searchScopeList, {
      value: drawerValues.searchScope,
    }),
    'label'
  );

  const sortingLabel = get(
    find(sortByList, {
      value: drawerValues.sortBy,
    }),
    'label'
  );

  const selectedWeldTypes = map(drawerValues.weldTypes, (item: any) => {
    return get(
      find(weldTypes, {
        value: item,
      }),
      'label'
    );
  });

  const selectedWeldStatusList = map(drawerValues.weldStatusList, (item: any) => {
    return get(
      find(weldStatusList, {
        value: item,
      }),
      'label'
    );
  });
  const selectedWeldSizeList = map(drawerValues.weldSizeList, (item: any) => {
    return get(
      find(weldSizeList, {
        value: item,
      }),
      'label'
    );
  });

  const filterByLabel = [...selectedWeldTypes, ...selectedWeldStatusList, ...selectedWeldSizeList];
  if (drawerValues.isPriority) filterByLabel.push(t`Priority Welds`);

  return (
    <Fragment>
      {!weldList?.length && !loading && !error && location.pathname === '/welds' && (
        <WeldDetailContainerStyled>
          <EmptyWeldDetailIcon />
          <Box mt={{ sm: '700' }} px={{ sm: '600' }}>
            <Text size="400" family="graphik" color="grey.600">
              <Trans>Please select a weld to view details</Trans>
            </Text>
          </Box>
        </WeldDetailContainerStyled>
      )}
      <WeldListContainerStyled data-testid="weld-list-container" isDrawerOpen={visible}>
        <WeldListHeader>
          <Box px={{ sm: '300' }} pt={{ sm: '500' }} py={{ sm: '550' }}>
            <Heading weight="600" size="600">
              {searchingLabel} {totalWelds ? `(${totalWelds})` : ''}
            </Heading>
          </Box>
          <SearchFieldStyled>
            <SearchFieldComponentStyled
              size={TextFieldSize.LARGE}
              iconSize={IconSize.MEDIUM}
              placeholder={t`Search`}
              type="search"
              onChange={(e) => {
                setSearchQuery(e.target.value);
              }}
              value={searchQuery}
            />
            <Box
              ml={{ sm: '400' }}
              css={`
                max-width: ${({ theme }) => theme.spacings[800]};
              `}
            >
              <SearchCanvasButtonStyled
                data-testid="button-search-canvas"
                isDefaultFilterChange={!isEqual(drawerValues, defaultValues)}
                onClick={toggle}
                appearance={ButtonAppearance.SECONDARY}
                size={ButtonSize.LARGE}
              >
                <StyledIcon
                  icon="sliders-v"
                  size={IconSize.LARGE}
                  fillColor={!isEqual(drawerValues, defaultValues) ? 'white' : ''}
                />
              </SearchCanvasButtonStyled>
            </Box>
          </SearchFieldStyled>

          <Box
            flexDirection={{
              sm: 'column',
            }}
            pr={{ sm: '600' }}
          >
            <Box mb={{ sm: '200' }}>
              <Text size="250" weight="600">
                <Trans>Sorting By </Trans> :&nbsp;
                <Text weight="400" data-testid="sorting-text">
                  {sortingLabel}
                  <Trans>{drawerValues.sortDir == SortOrder.ASC ? '(Ascending)' : '(Descending)'}</Trans>
                </Text>
              </Text>
            </Box>
            <Box mb={{ sm: '200' }}>
              <Text size="250" weight="600">
                <Trans>Searching </Trans> :&nbsp;
                <Text weight="400" data-testid="searching-text">
                  {searchingLabel}
                </Text>
              </Text>
            </Box>

            {searchingByLabel && searchingByLabel.length > 0 && (
              <Box mb={{ sm: '200' }}>
                <TextWithEllipsis size="250" weight="600" ellipsisPlacement={EllipsisPlacement.END}>
                  <Trans>Searching By</Trans>
                  :&nbsp;
                  <Text weight="400" title={compact(searchingByLabel)?.join(' , ')} data-testid="searching-by-text">
                    {searchingByLabel.length > 0
                      ? searchingByLabel.length === searchByList.length
                        ? t`Search All`
                        : compact(searchingByLabel)?.join(' , ')
                      : ''}
                  </Text>
                </TextWithEllipsis>
              </Box>
            )}
            {filterByLabel && filterByLabel.length > 0 && (
              <Box mb={{ sm: '200' }}>
                <TextWithEllipsis size="250" weight="600" ellipsisPlacement={EllipsisPlacement.END}>
                  <Trans>Filtering By</Trans>
                  :&nbsp;
                  <Text weight="400" title={compact(filterByLabel)?.join(' , ')} data-testid="filter-by-text">
                    {compact(filterByLabel)?.join(' , ')}
                  </Text>
                </TextWithEllipsis>
              </Box>
            )}
          </Box>
        </WeldListHeader>

        <ListStyled
          onScroll={(e) => {
            handleScroll(e);
          }}
        >
          {weldList.length > 0 &&
            map(weldList, (weld: Weld) => {
              const encodedId = encodeURIComponent(weld.id);
              return (
                <NavLinkStyled to={`/welds/${encodedId}`} key={encodedId}>
                  <ErrorBoundary>
                    <WeldItemCard weld={weld} />
                  </ErrorBoundary>
                </NavLinkStyled>
              );
            })}
          {(networkStatus === NetworkStatus.refetch || loading || projectLoading) && (
            <Box
              p={{ sm: '500' }}
              alignItems={{ sm: 'center' }}
              justifyContent={{ sm: 'center' }}
              flexGrow={{ sm: 1 }}
              css={weldList.length === 0 ? 'height: 100%;' : ''}
            >
              <Spinner size={SpinnerSize.LARGE} />
            </Box>
          )}
          {!loading && error && (
            <Box
              alignItems={{ sm: 'center' }}
              justifyContent={{ sm: 'center' }}
              flexGrow={{ sm: 1 }}
              flexDirection={{ sm: 'column' }}
              p={{ sm: '500' }}
            >
              <Box pb={{ sm: '500' }}>
                <Text>
                  <Trans>There was a problem loading Welds. Please click the button below to try again.</Trans>
                </Text>
              </Box>
              <Button onClick={() => refetch()}>
                <Box mr={{ sm: '400' }}>
                  <Icon icon="undo" />
                </Box>
                <Trans>Retry</Trans>
              </Button>
            </Box>
          )}
          {called && !loading && !error && weldList.length === 0 && (
            <Box
              flexDirection={{ sm: 'column' }}
              alignItems={{ sm: 'center' }}
              pt={{ sm: '900' }}
              data-testid="weld-list-empty"
            >
              <Heading size="400" weight="600" color="grey.600">
                <Trans> No welds found.</Trans>
              </Heading>
              <Box mt={{ sm: '700' }} mb={{ sm: '600' }} px={{ sm: '600' }}>
                <Text
                  size="400"
                  family="graphik"
                  color="grey.600"
                  //  line height is required because when sentence wraps in two lines gap between both lines are required.
                  style={{
                    textAlign: 'center',
                    lineHeight: '1.57',
                  }}
                >
                  <Trans>Try changing your search criteria or editing your filters.</Trans>
                </Text>
              </Box>
              <FilterIcon
                style={{
                  width: '124px',
                  height: '110px',
                }}
              />
              {drawerValues?.searchScope && (
                <Box mt={{ sm: '500' }}>
                  <Button
                    appearance={ButtonAppearance.SECONDARY}
                    onClick={() =>
                      setDrawerValues(() => {
                        persistedDrawerValues.current = {
                          ...drawerValues,
                          searchScope: DrawerSearchScope.ALL,
                        };
                        return persistedDrawerValues.current;
                      })
                    }
                  >
                    <Box px={{ sm: '700' }} py={{ sm: '700' }}>
                      <Text color="blue.800" weight="600">
                        <Trans>View All Welds </Trans>
                      </Text>
                    </Box>
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </ListStyled>
      </WeldListContainerStyled>
      <Suspense fallback={<Spinner size={SpinnerSize.LARGE} />}>
        {visible && (
          <WeldSearchDrawer
            close={close}
            initialValues={drawerValues}
            onSubmit={onDrawerSubmit}
            drawerForDashboard={false}
            personaType={user?.crewMember?.persona}
          />
        )}
      </Suspense>
    </Fragment>
  );
};
